import { setCookie } from '@/lib/cookies'
import { queryClient } from '@/main'
import { useAppStore } from './app-store'
import type { Locale } from 'shared/types'
import { isLocale } from 'shared/locales'
import { i18n, setupI18n, type I18n, type Messages } from '@lingui/core'
import { useLingui } from '@lingui/react'

export function useTranslation() {
  const { i18n: activeI18n } = useLingui()
  const setStore = useAppStore((state) => state.setStore)

  const language: Locale = isLocale(activeI18n.locale) ? activeI18n.locale : 'es-ES'

  const changeLanguage = (newLocale: string) => {
    loadTranslation(newLocale as Locale)
    setStore({ locale: newLocale as Locale })
    setCookie('language', newLocale)
    queryClient.invalidateQueries()
  }

  return {
    language,
    changeLanguage,
    languages: {
      'es-ES': 'Español',
      'ca-ES': 'Català',
      'en-US': 'English',
      'pt-BR': 'Português',
    },
  }
}

// One instance per SERVER request: a shared catalog is mutable global state, so interleaved SSR renders clobber
// each other's active locale and the client then hydrates against the wrong language (#418).
export function createLocaleI18n(): I18n {
  return typeof document === 'undefined' ? setupI18n() : i18n
}

/**
 * Load and activate a locale's catalog on the instance that will render it, and ONLY that one.
 *
 * On the client `createLocaleI18n` hands back the shared global, so this activates it exactly as before.
 * On the server `instance` is request-scoped and the global is deliberately left alone: activating
 * process-wide state per request let concurrent renders clobber each other's locale, which is the race
 * {@link createLocaleI18n} exists to contain. The remaining global readers only run on the client.
 */

/**
 * Catalog memo, production only. The `import()` below resolves an already-evaluated module every call
 * after the first, but the machinery is not free: ~1.2µs per repeat import in Bun against ~3ns for a
 * memoized reference, and `createRouter` calls it once per SSR request — ~0.5% of on-CPU on /report.
 *
 * DEV is excluded so editing a `.po` file still hot-reloads; `import.meta.env.DEV` is statically false
 * in the build, so the branch is eliminated in production.
 */
const catalogCache = new Map<Locale, Messages>()

export async function loadTranslation(locale: Locale, instance?: I18n): Promise<I18n> {
  let messages = import.meta.env.DEV ? undefined : catalogCache.get(locale)
  if (!messages) {
    ;({ messages } = (await import(`../locales/${locale}.po`)) as { messages: Messages })
    if (!import.meta.env.DEV) catalogCache.set(locale, messages)
  }
  const target = instance ?? i18n
  // Still per-request: `instance` is request-scoped on the server (see createLocaleI18n), so the catalog
  // must be activated on it every time. Only the module resolution is memoized, never the activation.
  target.loadAndActivate({ locale, messages })
  return target
}
